if (!customElements.get('product-bundle')) {
  customElements.define(
    'product-bundle',
    class ProductBundle extends HTMLElement {
      constructor() {
        super();
        this.bundleItems = this.querySelectorAll('[data-item]');
        this.saveContainers = this.querySelectorAll('[data-save-value]');
        this.totalContainer = this.querySelector('[data-total]');
        this.totalCompareContainer = this.querySelector('[data-total-compare-price]');
        this.selectedQuantityContainer = this.querySelector('[data-quantity]');
        this.atcMessageContainer = this.querySelector('[data-atc-text]');
        this.addToCartButton = this.querySelector('[data-atc]');
        this.slider = this.querySelector('[data-slider]');
      }

      get localization() {
        return JSON.parse(atob(this.dataset.localization));
      }

      get currency() {
        return this.dataset.currency;
      }

      get combinationsMap() {
        const combinations = this.querySelector('[data-combinations-map]');
        return JSON.parse(combinations.innerText);
      }

      get bundleSize() {
        return Number(this.dataset.bundleSize);
      }

      get activeOptionsQuantity() {
        return this.querySelectorAll('[data-item].active').length;
      }

      get sliderType() {
        return this.slider?.dataset.sliderType;
      }

      connectedCallback() {
        if (this.bundleItems.length === 0) return;

        this.initSlider();
        this.bundleItems.forEach((item) => item.addEventListener('click', () => this.selectOption(item)));
        this.addToCartButton.addEventListener('click', () => this.handleAddToCart());
        this.setupStickyHide();

        requestAnimationFrame(() => this.equalizeHeaderHeights());
        this._headerResizeObserver = new ResizeObserver(() => this.equalizeHeaderHeights());
        this._headerResizeObserver.observe(this);
      }

      disconnectedCallback() {
        this.bundleItems.forEach((item) => item.removeEventListener('click', () => this.selectOption(item)));
        this.addToCartButton.removeEventListener('click', () => this.handleAddToCart());
        if (this._stickyCheckOverlap) {
          window.removeEventListener('scroll', this._stickyCheckOverlap);
        }
        if (this._headerResizeObserver) {
          this._headerResizeObserver.disconnect();
        }
      }

      selectOption(item) {
        item.classList.toggle('active');
        this.addToCartState();
        this.setDiscounts();
        this.updatePrice();
        this.updateDiscount();
        this.addToCartTextHandler();
        this.calculateTotal();
        this.calculateTotalComparePrice();
        this.calculateComparePrice();
        this.calculateSave();
      }

      setDiscounts() {
        if (this.activeOptionsQuantity <= 1) {
          this.bundleItems.forEach((item) => {
            item.dataset.discount = '';
          });
        } else {
          this.bundleItems.forEach((item) => {
            item.dataset.discount = Number(item.dataset.itemDiscount) > 0 ? item.dataset.itemDiscount : '';
          });
        }
      }

      addToCartTextHandler() {
        if (this.activeOptionsQuantity < this.bundleSize) {
          this.selectedQuantityContainer.innerHTML = this.activeOptionsQuantity;
          this.selectedQuantityContainer.classList.remove('hidden');
          this.atcMessageContainer.innerHTML = this.activeOptionsQuantity === 1 ? this.localization.single : this.localization.multi;
        } else {
          this.selectedQuantityContainer.classList.add('hidden');
          this.atcMessageContainer.innerHTML = this.localization.bundle;
        }
      }

      updatePrice() {
        this.bundleItems.forEach((item) => {
          const priceContainer = item.querySelector('[data-price-value]');
          const price = Number(item.dataset.price);
          const discount = item.dataset.discount;
          const discountValue = discount ? Number(discount) / 100 : 0;
          const priceReduction = price * discountValue;
          const newPriceValue = ((price - priceReduction) / 100).toFixed(2);
          priceContainer.innerHTML = `${newPriceValue.replace('.', ',')} ${this.currency}`;
          priceContainer.dataset.priceValue = newPriceValue;
        });
      }

      updateDiscount() {
        this.bundleItems.forEach((item) => {
          const discountContainer = item.querySelector('[data-discount-value]');
          if (!discountContainer) return;
          const discount = item.dataset.discount;
          discountContainer.innerHTML = discount ? `-${discount}%` : '';
        });
      }

      calculateTotal() {
        let total = 0;
        const selectedItems = this.querySelectorAll('[data-item].active');
        if (selectedItems.length > 0) {
          selectedItems.forEach((item) => {
            const price = item.querySelector('[data-price-value]');
            const priceValue = Number(price.dataset.priceValue);
            total += priceValue;
          });
        }
        this.totalContainer.innerHTML = `${total.toFixed(2).replace('.', ',')} ${this.currency}`;
      }

      calculateSave() {
        let save = 0;
        const activeItems = this.querySelectorAll('[data-item].active');
        if (activeItems.length > 0) {
          activeItems.forEach((item) => {
            const originalPrice = Number(item.dataset.price / 100);
            const currentPriceContainer = item.querySelector('[data-price-value]');
            const currentPrice = Number(currentPriceContainer.dataset.priceValue);
            const savings = originalPrice - currentPrice;
            save += savings;
          });
        }

        this.saveContainers.forEach((container) => {
          container.innerHTML = `${save.toFixed(2).replace('.', ',')} ${this.currency}`;
        });
      }

      calculateTotalComparePrice() {
        let compareTotal = 0;
        const selectedItems = this.querySelectorAll('[data-item].active');
        selectedItems.forEach((item) => {
          compareTotal += Number(item.dataset.price) / 100;
        });
        if (this.totalCompareContainer) {
          this.totalCompareContainer.innerHTML = `${compareTotal.toFixed(2).replace('.', ',')} ${this.currency}`;
        }
      }

      calculateComparePrice() {
        this.bundleItems.forEach((item) => {
          const comparePriceContainer = item.querySelector('[data-comapre-price]');
          if (!comparePriceContainer) return;
          const originalPrice = Number(item.dataset.price) / 100;
          const discount = item.dataset.discount;
          if (discount && Number(discount) > 0) {
            comparePriceContainer.innerHTML = `${originalPrice.toFixed(2).replace('.', ',')} ${this.currency}`;
          } else {
            comparePriceContainer.innerHTML = '';
          }
        });
      }

      addToCartState() {
        this.addToCartButton.disabled = this.activeOptionsQuantity === 0 ? true : false;
      }

      async handleAddToCart() {
        const url = `${Shopify.routes.root}cart/add.js`;
        const request = {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': 'no-cors',
          },
          body: JSON.stringify(this.addToCartResponse()),
        };

        const response = await fetch(url, request);
        const result = await response.json();
        if (response.ok) {
          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          cartContent['sections'] = result['sections'];
          const items = result.hasOwnProperty('items') ? result['items'] : [result];
          document.documentElement.dispatchEvent(
            new CustomEvent('variant:add', {
              bubbles: true,
              detail: {
                items,
                cart: cartContent,
              },
            }),
          );
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: {
                baseEvent: 'variant:add',
                cart: cartContent,
              },
            }),
          );
        }
      }

      addToCartResponse() {
        const data = {
          items: [],
          sections: '',
        };
        let sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        data.sections = [...sectionsToBundle].join(',');
        const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const selectedItems = [...this.querySelectorAll('[data-item].active')];
        if (selectedItems.length === 1) {
          data.items.push({
            id: Number(selectedItems[0].dataset.item),
            quantity: 1,
          });
        } else {
          selectedItems.forEach((item) => {
            const object = {
              id: Number(item.dataset.item),
              quantity: 1,
              properties: {
                _bundle_id: bundleId,
                _is_bundle_item: 'true',
                _bundle_item_discount: item.dataset.discount || '0',
              },
            };
            if (this.bundleName() !== '') object.properties['Bundle'] = this.bundleName();

            data.items.push(object);
          });
        }
        return data;
      }

      bundleName() {
        const selectedItems = [...this.querySelectorAll('[data-item].active')];
        if (selectedItems.length === 0) return '';
        if (selectedItems.length === this.bundleItems.length) return this.combinationsMap[0];

        const titles = selectedItems.map((item) => item.dataset.partTitle);
        return this.combinationsMap.find((combination) => titles.every((title) => combination.includes(title))) ?? '';
      }

      initSlider() {
        if (!this.slider) return;
        if (this.sliderType === 'mobile' && window.innerWidth > 1180) return;
        const options = {
          slidesPerView: 2,
          slidesPerMove: 1,
          spaceBetween: 10,
          mousewheel: { forceToAxis: true },
          breakpoints: {
            700: {
              slidesPerView: 2.5,
            },
            1025: {
              slidesPerView: 2.75,
            },
            1181: {
              slidesPerView: 3,
            },
            1680: {
              slidesPerView: this.bundleSize,
            },
          },
          pagination: {
            el: this.slider.querySelector('.swiper-pagination'),
            type: 'bullets',
            clickable: true,
          },
        };
        new window.Swiper(this.slider, options);
      }

      setupStickyHide() {
        const stickyEl = document.querySelector('product-sticky-add-to-cart');
        if (!stickyEl) return;

        const checkOverlap = () => {
          const bundleRect = this.getBoundingClientRect();
          const stickyRect = stickyEl.getBoundingClientRect();
          const offset = this.clientHeight / 3;
          const overlaps = bundleRect.top < stickyRect.bottom && bundleRect.bottom + offset > stickyRect.top;
          stickyEl.classList.toggle('bundle-overlap', overlaps);
        };

        this._stickyCheckOverlap = checkOverlap;
        window.addEventListener('scroll', checkOverlap, { passive: true });
        checkOverlap();
      }

      equalizeHeaderHeights() {
        const headers = this.querySelectorAll('.product-bundle__item--header');
        if (headers.length === 0) return;
        headers.forEach((h) => (h.style.height = ''));
        const maxHeight = Math.max(...[...headers].map((h) => h.offsetHeight));
        headers.forEach((h) => (h.style.minHeight = `${maxHeight}px`));
      }
    },
  );
}
